import React, { useState, useEffect, useContext } from 'react';
import { Link } from "react-router-dom";
import 'bulma/css/bulma.min.css';

import { updateBeamline } from '../api/api.ts';
import { BeamlineContext, BeamlineLoadingState } from '../beamlineProvider.tsx';
import { LoadingBanner, LoadingState } from '../components/loadingBanner.tsx';
import { InputEditable, ValidationStatus } from '../components/inputEditable.tsx';
import BeamlineHeader from './../components/beamlineHeader.tsx';
import ScanTypeTable from './scanTypeTable.tsx';
import ParameterTable from './parameterTable.tsx';


enum SelectedTab { GeneralInformation, Parameters, ScanTypes };


const ManageBeamlineHome: React.FC = () => {

  const beamlineContext = useContext(BeamlineContext);

  const [selectedTab, setSelectedTab] = useState<SelectedTab>(SelectedTab.GeneralInformation);
  const [description, setDescription] = useState<string>("");
  const [externalLink, setExternalLink] = useState<string>("");
  const [setNoun, setSetNoun] = useState<string>("");

  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");


  useEffect(() => {
    if (beamlineContext.loadingState == BeamlineLoadingState.Failed) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Failed to load Beamline. Are you sure you're still logged in?");
      return;
    } 
    if (beamlineContext.loadingState == BeamlineLoadingState.Succeeded) {
      setLoading(LoadingState.Success);

      setDescription(beamlineContext.beamline?.description || "");
      setExternalLink(beamlineContext.beamline?.external_link || "");
      setSetNoun(beamlineContext.beamline?.set_noun || "");

      return;
    }
    setLoading(LoadingState.Loading);
    setLoadingMessage("Loading Beamline...");
  }, [beamlineContext.changeCounter, beamlineContext.loadingState]);


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loading != LoadingState.Success) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }


  async function descriptionEditSubmit(value: string) {
    const result = await updateBeamline({slug: beamlineContext.beamline!.slug, description: value});
    if (result.success) {
      beamlineContext.beamline!.description = value;
      setDescription(value);
      return { status: ValidationStatus.Success };
    } else {
      setDescription(beamlineContext.beamline!.description || "");
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  async function externalLinkEditSubmit(value: string) {
    const result = await updateBeamline({slug: beamlineContext.beamline!.slug, external_link: value});
    if (result.success) {
      beamlineContext.beamline!.external_link = value;
      setExternalLink(value);
      return { status: ValidationStatus.Success };
    } else {
      setExternalLink(beamlineContext.beamline!.external_link || "");
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  async function setNounEditSubmit(value: string) {
    if (value.trim().length == 0) {
      value = "set";
    }
    const result = await updateBeamline({slug: beamlineContext.beamline!.slug, set_noun: value});
    if (result.success) {
      beamlineContext.beamline!.set_noun = value;
      setSetNoun(value);
      return { status: ValidationStatus.Success };
    } else {
      setSetNoun(beamlineContext.beamline!.set_noun || "set");
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  return (
    <>
      <BeamlineHeader />

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <nav className="breadcrumb is-medium" aria-label="breadcrumbs">
              <ul>
                <li><Link to={ "/" }>Beamlines</Link></li>
                <li className="is-active"><Link to={ "/beamline/" + beamlineContext.beamline!.slug }>{ beamlineContext.beamline!.name }</Link></li>
              </ul>
            </nav>
          </div>
        </div>
        <div className="level-right">
        </div>
      </nav>

      <div className="tabs is-boxed">
        <ul>
          <li className={ selectedTab == SelectedTab.GeneralInformation ? "is-active" : "" }
              onClick={ () => setSelectedTab(SelectedTab.GeneralInformation) }>
            <a>
              <span>General Information</span>
            </a>
          </li>
          <li className={ selectedTab == SelectedTab.Parameters ? "is-active" : "" }
              onClick={ () => setSelectedTab(SelectedTab.Parameters) }>
            <a>
              <span>Parameters</span>
            </a>
          </li>
          <li className={ selectedTab == SelectedTab.ScanTypes ? "is-active" : "" }
              onClick={ () => setSelectedTab(SelectedTab.ScanTypes) }>
            <a>
              <span>Scan Types</span>
            </a>
          </li>
        </ul>
      </div>

      <div style={{ display: selectedTab == SelectedTab.GeneralInformation ? "block" : "none" }}>

        <div className="block">
          <InputEditable
              elementId="beamline-description"
              value={ description }
              placeholder={`Beamline description`}
              showHelp={true}
              label="Beamline Description"
              editFunctions={{
                validator: async () => { return { status: ValidationStatus.Success } },
                submit: descriptionEditSubmit
              }} />

          <InputEditable
              elementId="beamline-external-link"
              value={ externalLink }
              placeholder={`External link for more information`}
              showHelp={true}
              label="External link"
              editFunctions={{
                validator: async () => { return { status: ValidationStatus.Success } },
                submit: externalLinkEditSubmit
              }} />

          <InputEditable
              elementId="beamline-setnoun"
              value={ setNoun }
              placeholder="Noun for sets"
              showHelp={true}
              label="Noun to use for sets of samples (e.g. 'bar', 'batch', 'plate'...)"
              editFunctions={{
                validator: async () => { return { status: ValidationStatus.Success } },
                submit: setNounEditSubmit
              }} />
        </div>

      </div>
      <div style={{ display: selectedTab == SelectedTab.Parameters ? "block" : "none" }}>

        <div className="block">
          Parameters are the individual pieces of information users enter about Samples.
          Each beamline defines its own Parameters.
          You control which ones the users see by grouping them into one or more Scan Types.
        </div>

        <ParameterTable />

      </div>
      <div style={{ display: selectedTab == SelectedTab.ScanTypes ? "block" : "none" }}>

        <div className="block">
          When users create Samples, they choose a Scan Type for each Sample.
          Each Scan Type defines a list of Parameters that belong to it.
          Here you can create Scan Types and add Parameters to them, specifying additional settings.
        </div>

        <ScanTypeTable />

      </div>
    </>
  )
}


export default ManageBeamlineHome;
